import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRotate } from '@fortawesome/free-solid-svg-icons'
import { executeCapability } from '../../../utils/capabilityExecute'
import { errorMessage } from '../types'
import TallyToolbar from '../TallyToolbar'
import FleetCardList from './FleetCardList'
import FleetDetailPanel, { type FleetMaintenancePayload } from './FleetDetailPanel'
import type { FleetUnit } from './fleetTypes'
import { fleetMatchesSearch } from './fleetTypes'

type FleetTabProps = {
  canManage: boolean
  active: boolean
}

function asFleetUnit(raw: unknown): FleetUnit {
  const base = (raw && typeof raw === 'object' ? raw : {}) as Partial<FleetUnit>
  return {
    ...base,
    _id: String(base._id || ''),
    vin: String(base.vin || ''),
    name: String(base.name || ''),
  }
}

export const FleetTab: React.FC<FleetTabProps> = ({ canManage, active }) => {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<string[]>([])
  const [units, setUnits] = useState<FleetUnit[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const loadFleet = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await executeCapability({
        app: 'tally',
        capability: 'fleet.list',
        input: { includeArchived },
      })
      const list = Array.isArray(data) ? data.map(asFleetUnit) : []
      setUnits(list)
    } catch (e) {
      setError(errorMessage(e, 'Failed to load fleet'))
      setUnits([])
    } finally {
      setLoading(false)
    }
  }, [includeArchived])

  useEffect(() => {
    if (!active) return
    void loadFleet()
  }, [active, loadFleet])

  useEffect(() => {
    if (!selectedId) return
    if (!units.some((u) => u._id === selectedId)) setSelectedId(null)
  }, [units, selectedId])

  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 4000)
    return () => window.clearTimeout(t)
  }, [toast])

  const selectedUnit = useMemo(
    () => units.find((u) => u._id === selectedId) || null,
    [units, selectedId],
  )

  const filteredUnits = useMemo(() => {
    const queries = [...filters]
    if (search.trim()) queries.push(search.trim())
    if (!queries.length) return units
    return units.filter((unit) => fleetMatchesSearch(unit, queries))
  }, [units, filters, search])

  const sortedUnits = useMemo(
    () => [...filteredUnits].sort((a, b) => a.name.localeCompare(b.name)),
    [filteredUnits],
  )

  const replaceUnit = useCallback((updated: FleetUnit) => {
    setUnits((prev) => prev.map((u) => (u._id === updated._id ? updated : u)))
  }, [])

  const handleSync = useCallback(async () => {
    setSyncing(true)
    setError(null)
    try {
      const stats = (await executeCapability({
        app: 'tally',
        capability: 'fleet.syncFromLinxup',
        input: { refreshCache: true },
      })) as { created?: number; updated?: number; inactivated?: number; totalEntities?: number }
      await loadFleet()
      setToast(
        `Synced ${stats?.totalEntities ?? 0} Linxup units · ${stats?.created ?? 0} new · ${stats?.updated ?? 0} updated`,
      )
    } catch (e) {
      setError(errorMessage(e, 'Failed to sync fleet from Linxup'))
    } finally {
      setSyncing(false)
    }
  }, [loadFleet])

  const handleArchive = useCallback(
    async (unit: FleetUnit) => {
      setBusy(true)
      try {
        const updated = asFleetUnit(
          await executeCapability({
            app: 'tally',
            capability: 'fleet.archive',
            input: { id: unit._id },
          }),
        )
        if (includeArchived) replaceUnit(updated)
        else {
          setUnits((prev) => prev.filter((u) => u._id !== unit._id))
          setSelectedId(null)
        }
      } catch (e) {
        setError(errorMessage(e, 'Failed to archive unit'))
      } finally {
        setBusy(false)
      }
    },
    [includeArchived, replaceUnit],
  )

  const handleUnarchive = useCallback(
    async (unit: FleetUnit) => {
      setBusy(true)
      try {
        const updated = asFleetUnit(
          await executeCapability({
            app: 'tally',
            capability: 'fleet.unarchive',
            input: { id: unit._id },
          }),
        )
        replaceUnit(updated)
      } catch (e) {
        setError(errorMessage(e, 'Failed to unarchive unit'))
      } finally {
        setBusy(false)
      }
    },
    [replaceUnit],
  )

  const handleAddMaintenance = useCallback(
    async (unit: FleetUnit, payload: FleetMaintenancePayload) => {
      const updated = asFleetUnit(
        await executeCapability({
          app: 'tally',
          capability: 'fleet.addMaintenance',
          input: { id: unit._id, ...payload },
        }),
      )
      replaceUnit(updated)
    },
    [replaceUnit],
  )

  const handleUpdateUnitImages = useCallback(
    async (unit: FleetUnit, imageUrls: string[]) => {
      const updated = asFleetUnit(
        await executeCapability({
          app: 'tally',
          capability: 'fleet.update',
          input: { id: unit._id, update: { imageUrls } },
        }),
      )
      replaceUnit(updated)
    },
    [replaceUnit],
  )

  const handleUpdateNotes = useCallback(
    async (unit: FleetUnit, notes: string) => {
      const updated = asFleetUnit(
        await executeCapability({
          app: 'tally',
          capability: 'fleet.update',
          input: { id: unit._id, update: { notes } },
        }),
      )
      replaceUnit(updated)
    },
    [replaceUnit],
  )

  const handleUpdateComplianceImages = useCallback(
    async (unit: FleetUnit, kind: 'registration' | 'insurance', imageUrls: string[]) => {
      const field = kind === 'registration' ? 'registrationImageUrls' : 'insuranceImageUrls'
      const updated = asFleetUnit(
        await executeCapability({
          app: 'tally',
          capability: 'fleet.update',
          input: { id: unit._id, update: { [field]: imageUrls } },
        }),
      )
      replaceUnit(updated)
    },
    [replaceUnit],
  )

  const handleUpdateAssignedEmployee = useCallback(
    async (unit: FleetUnit, assignedEmployeeId: string | null) => {
      const updated = asFleetUnit(
        await executeCapability({
          app: 'tally',
          capability: 'fleet.update',
          input: { id: unit._id, update: { assignedEmployeeId: assignedEmployeeId || '' } },
        }),
      )
      replaceUnit(updated)
    },
    [replaceUnit],
  )

  if (!active) return null

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {toast ? (
        <div className="alert alert-success mb-2 shrink-0 py-2 text-sm">
          <span>{toast}</span>
        </div>
      ) : null}

      <TallyToolbar
        isAssetTab={false}
        searchMode="fleet"
        search={search}
        onSearchChange={setSearch}
        onSearchCommit={(value) => {
          setFilters((f) => (f.includes(value) ? f : [...f, value]))
          setSearch('')
        }}
        filters={filters}
        onRemoveFilter={(idx) => setFilters((f) => f.filter((_, i) => i !== idx))}
        onClearSearch={() => {
          setSearch('')
          setFilters([])
        }}
        resultCount={sortedUnits.length}
        totalCount={units.length}
        collapsedView={false}
        onToggleCollapsedView={() => {}}
        canManage={false}
        onAdd={() => {}}
        hideViewToggle
        trailing={
          <>
            <label className="label cursor-pointer gap-2 py-0 hidden sm:flex">
              <span className="label-text text-xs whitespace-nowrap">Show archived</span>
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
            </label>
            {canManage ? (
              <button
                type="button"
                className="btn btn-outline h-11 min-h-0 rounded-box px-3"
                disabled={syncing}
                onClick={() => void handleSync()}
                title="Refresh Linxup cache and sync fleet"
              >
                <FontAwesomeIcon icon={faRotate} className={syncing ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">{syncing ? 'Syncing…' : 'Sync'}</span>
              </button>
            ) : null}
          </>
        }
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-1">
          <FleetCardList
            items={sortedUnits}
            loading={loading || syncing}
            error={error}
            selectedId={selectedId}
            onSelect={(unit) => setSelectedId(unit._id)}
          />
        </div>

        {selectedUnit ? (
          <>
            <FleetDetailPanel
              unit={selectedUnit}
              canManage={canManage}
              busy={busy}
              presentation="docked"
              onClose={() => setSelectedId(null)}
              onArchive={(u) => void handleArchive(u)}
              onUnarchive={(u) => void handleUnarchive(u)}
              onAddMaintenance={handleAddMaintenance}
              onUpdateUnitImages={handleUpdateUnitImages}
              onUpdateNotes={handleUpdateNotes}
              onUpdateComplianceImages={handleUpdateComplianceImages}
              onUpdateAssignedEmployee={handleUpdateAssignedEmployee}
            />
            <FleetDetailPanel
              unit={selectedUnit}
              canManage={canManage}
              busy={busy}
              presentation="overlay"
              onClose={() => setSelectedId(null)}
              onArchive={(u) => void handleArchive(u)}
              onUnarchive={(u) => void handleUnarchive(u)}
              onAddMaintenance={handleAddMaintenance}
              onUpdateUnitImages={handleUpdateUnitImages}
              onUpdateNotes={handleUpdateNotes}
              onUpdateComplianceImages={handleUpdateComplianceImages}
              onUpdateAssignedEmployee={handleUpdateAssignedEmployee}
            />
          </>
        ) : null}
      </div>
    </div>
  )
}

export default FleetTab
